/**
 * BookingSection: 商談形式選択・メッセージ入力・日程調整（空き枠選択／リクエスト）・
 * 完了表示を1つのカードにまとめて構築する共通ロジック。
 * PreBookingForm（製品詳細・電子ブック閲覧・お問い合わせ完了）・SurveyPopup（企業詳細、
 * アンケート回答後の遷移先）の両方から利用する（design-mockups/*.html準拠、2026-08-19デザイン更新）。
 *
 * 呼び出し元がWidgetPanel（左下固定パネル or 画面中央モーダル）とCalendarModal
 * （候補日時展開用の中央モーダル）を構築済みであることを前提とする。
 */

import { createElement } from "../core/shadowDomUtil";
import { showErrorMessageBanner } from "../core-ui/errorMessageBannerUi";
import { renderLoadingSpinner, setButtonLoading } from "../core-ui/loadingSpinnerUi";
import { requestAppointmentSlotsForSingle } from "../w4-appointment/requestAppointmentSlots";
import { confirmAppointment } from "../w4-appointment/confirmAppointment";
import { submitAppointmentRequest } from "../w4-appointment/submitAppointmentRequest";
import { renderSlotPicker, formatSlotButtonLabel } from "../core-ui/calendarGridUi";
import { renderDesiredDateTimeCalendarInputs } from "../core-ui/desiredDateTimeInputUi";
import { renderMeetingTypeSelection } from "./meetingTypeSelectionUi";
import { renderMessageInput } from "./messageInputUi";
import { createWidgetPanel, buildWidgetLoginUrl, buildWidgetRegisterUrl } from "../core-ui/widgetPanelUi";
import { serviceSiteHostBase } from "../core-ui/loginPromptOverlayUi";
import type { CalendarModalHandle } from "../core-ui/calendarModalUi";
import { renderMeetingModeLabel } from "../core-ui/meetingModeLabelUi";
import { renderCalendarAddActions } from "../core-ui/calendarAddActionsUi";
import { formatSlotLabel } from "../core-ui/datetime";
import { renderConsentText } from "../core-ui/consentTextUi";
import type { AppointmentSlot, MeetingType } from "../types";
import { isLoggedIn } from "../core/authState";
import { renderLoggedOutMosaic } from "../core-ui/loggedOutMosaicUi";
import {
  trackMeetingTypeSelect,
  trackSlotSelect,
  trackAppointmentSubmit,
  trackAppointmentSubmitFailed,
} from "../w3-tracking/trackPageEvent";
import type { PageEventPayload } from "../types";

/** 空き枠取得エラー時の共通メッセージ（bookingSectionUi/multiCvAppointmentListUiで共有）。 */
export const SLOT_FETCH_ERROR_MESSAGE = "空き日程の取得に失敗しました。時間をおいて再度お試しください。";

/**
 * 同一企業への重複アポイント（BR-U2-18）時のエラーバナーを表示する。
 * 「こちら」を既存アポイントのキャンセル導線（`/?cancel_appointment_id={id}`）へのリンクにする。
 * bookingSectionUi/multiCvAppointmentListUiで共有する。
 *
 * <p><b>2026-09-08バグ修正（特設サイト対応）</b>: キャンセル導線のhrefを相対パスから、先頭 `pr.` を
 * 除いたサービスサイトホストの絶対URLに変更した。特設サイト（`pr.<host>`）で相対パス
 * `/?cancel_appointment_id=...` を踏むと `https://pr.<host>/?cancel_appointment_id=...` となり、
 * Apacheが `/pr` を前置してTomcatへプロキシするためキャンセルUIのあるトップページに解決されず404になる。
 * ログイン導線（`buildLoginRedirectUrl`）・中継API（`relayClient.toApiUrl`）と同じ `pr.` 除去方針の
 * `serviceSiteHostBase()` を用いてサービスサイト側へ遷移させる。
 */
export function showDuplicateCompanyAppointmentError(existingAppointmentId: string): void {
  showErrorMessageBanner({
    errorType: "BUSINESS",
    message: "同一企業へのアポイントは1件までとなります。再度お申し込みいただく場合は、",
    link: {
      linkText: "こちら",
      href: `${serviceSiteHostBase()}/?cancel_appointment_id=${encodeURIComponent(existingAppointmentId)}`,
      afterText: "からキャンセルの上、あらためてお手続きください。",
    },
  });
}

/** ISO8601日時文字列を「8/25(月) 15:00〜」形式に変換する（候補確定後の静的表示用）。 */
export function formatDesiredDateTimeLabel(iso: string): string {
  // JSTローカル文字列を文字列操作で整形する（`new Date(iso)` 不使用、TZ非依存。datetime-contract.md §4）。
  return formatSlotLabel(iso);
}

export interface BookingSectionOptions {
  memberGroupId: string;
  screenType: string;
  objectType: string;
  objectRootId: string;
  meetingModePolicy: "ONLINE_ONLY" | "ON_SITE_ONLY" | "HYBRID" | null | undefined;
  prFlg?: boolean | null;
  companyName?: string | null;
  contentName?: string | null;
  /**
   * 出展企業root_id（CV単位、任意）。効果分析ログのテナント境界確定に用いる（NFR-A3）。
   * ContentDisplayResult.companyRootId をそのまま渡す。WIDGET_VIEW と同様に各イベントへ付与する。
   */
  companyRootId?: number | null;
  /**
   * 表示優先度の区別（BR-U6-4）。true=メインCV、false=レコメンドCV。
   * ContentDisplayResult.isMainCvをそのまま渡す。isRecommendCv算出に使用する。
   */
  isMainCv?: boolean;
  /**
   * アンケート回答ログID（survey_response_log.id、任意）。アンケート回答経由でこの予約導線に進んだ場合のみ
   * `submitSurveyAnswer`の戻り値から引き継いで渡す。アンケートを経由しない導線ではundefined
   * （未指定＝紐付けなし。2026-09-24 BR-U2-21 誤紐付け修正）。
   */
  surveyResponseLogId?: string;
}

/**
 * 指定のカード要素へ、企業名・コンテンツ名の見出しを先頭に配置した上で、
 * 商談形式選択・メッセージ入力・日程調整UIを構築する。
 * カード自体（企業名・コンテンツ名を除く全て）は予約完了時にこの関数内で完了表示へ差し替えられる。
 */
export async function renderBookingSection(
  card: HTMLElement,
  panel: ReturnType<typeof createWidgetPanel>,
  calendarModal: CalendarModalHandle,
  options: BookingSectionOptions
): Promise<void> {
  const { memberGroupId, screenType, objectType, objectRootId, meetingModePolicy, prFlg, companyName, contentName, companyRootId, isMainCv, surveyResponseLogId } = options;

  // 効果分析ログ（BR-AN-1〜14）の共通文脈。取れる範囲の分析軸を各イベントへ付与する（BR-AN-8）。
  // companyRootId は WIDGET_VIEW と同様にテナント境界確定のため各イベントへ付与する（NFR-A3。
  // 中継は透過のみで解決しないため、タグが CV 単位の companyRootId を載せる）。
  // sessionId は各 track ラッパーが付与する。
  const analyticsCtx: PageEventPayload = { screenType, objectType, objectRootId, companyRootId, prFlg, isMainCv };

  const companyLabel = createElement("div", { className: "spica-company-name" });
  companyLabel.appendChild(document.createTextNode(companyName ?? "出展企業"));
  // FR-2（2026-09-11）: 未ログイン時は商談形式ラベル（オンライン/対面）を実データで出さない
  // （blur越しに透けて見えるため）。企業名・コンテンツ名は企業詳細等で公開済みのため残す。
  const loggedIn = isLoggedIn();
  if (loggedIn) {
    const meetingModeLabel = renderMeetingModeLabel(meetingModePolicy);
    if (meetingModeLabel) companyLabel.appendChild(meetingModeLabel);
  }
  card.appendChild(companyLabel);

  // 要件 FR-6（I261-77）: 未ログインの企業詳細（COMPANY）は企業名のみを表示する。resolve API は COMPANY でも
  // contentName に company_root.name を返す（ContentNameReader）ため、未ログイン時は製品名行を出さない
  // （企業名の重複表示を避ける）。ログイン済みの表示は従来どおり（変更しない）。
  if (contentName && (loggedIn || objectType !== "COMPANY")) {
    card.appendChild(createElement("div", { className: "spica-content-name", text: contentName }));
  }

  // FR-1/FR-2（2026-09-11）: 未ログイン時は空き枠取得API（/api/public/available-slots）を呼ばない。
  // 実データ（空き枠・商談形式）はログイン後にのみ扱い、未ログイン時は商談形式選択UIも描画せず
  // 日程調整画面ベースの固定モザイク（ダミー日程4マス＋案内文＋「ログイン」「新規会員登録」の2ボタン）のみ表示して早期returnする
  // （I261-77。従来の「全体ブラー＋ログインゲート重畳」は廃止）。LOGIN_PROMPT は従来どおり描画時に1回発火する。
  if (!loggedIn) {
    card.appendChild(renderLoggedOutMosaic({ loginUrl: buildWidgetLoginUrl("APPOINTMENT_BOOKING", analyticsCtx), registerUrl: buildWidgetRegisterUrl("APPOINTMENT_BOOKING") }));
    return;
  }

  // 効果分析ログ（BR-AN-2、2026-09-29）: ファネル起点は entry.ts の WIDGET_VIEW（sessionId 付与）に
  // 一本化した（WIDGET_OPEN は WIDGET_VIEW と発火が重複するため廃止）。ここでは起点イベントを発火しない。

  const meetingTypeSelection = renderMeetingTypeSelection(meetingModePolicy, (meetingType) => {
    // 効果分析ログ（イベントC、BR-AN-1）: 商談形式（オンライン/対面）の切替を発火する
    // （HYBRID時のみ onChange が呼ばれる。ONLINE_ONLY/ON_SITE_ONLY は選択の余地がないため発火しない）。
    trackMeetingTypeSelect({ ...analyticsCtx, meetingType });
    messageInput.updateMeetingType(meetingType);
    refreshConfirmButtonAction();
  });
  card.appendChild(meetingTypeSelection.element);

  const initialMeetingType = meetingTypeSelection.getSelectedMeetingType();
  const messageInput = renderMessageInput(initialMeetingType);

  let refreshConfirmButtonAction: () => void = () => {};

  // 空き枠取得中はスピナーを表示する（ユーザー向けローディングフィードバック）
  const loadingEl = renderLoadingSpinner({ text: "空き日程を取得しています..." });
  card.appendChild(loadingEl);

  // 空き枠はログイン済みユーザーのみ取得する（未ログインは上で早期return済み）
  const slotResult = await requestAppointmentSlotsForSingle(memberGroupId);

  // スピナーを除去
  loadingEl.remove();

  // APIエラー時は「取得に失敗しました」を表示し、リクエスト導線は出さない
  if (slotResult.hasError) {
    card.appendChild(createElement("p", { className: "spica-request-note", text: SLOT_FETCH_ERROR_MESSAGE }));
    return;
  }

  const slots = slotResult.slots;
  const bookingArea = createElement("div");

  // モック準拠の配置順: 商談形式→日程ラベル→スロット/リクエスト→メッセージ→確定ボタン→折りたたみ
  card.appendChild(bookingArea);

  if (slots.length === 0) {
    renderRequestSection({
      container: bookingArea,
      panel,
      calendarModal,
      meetingTypeSelection,
      messageInput,
      companyName,
      contentName,
      memberGroupId,
      onRequest: async (desiredDateTimes, meetingType, message, visitPreferredLocation) => {
        const result = await submitAppointmentRequest({
          memberGroupId,
          desiredDateTime1: desiredDateTimes.desiredDateTime1,
          desiredDateTime2: desiredDateTimes.desiredDateTime2,
          desiredDateTime3: desiredDateTimes.desiredDateTime3,
          screenType,
          objectType,
          objectRootId,
          meetingType,
          message,
          visitPreferredLocation,
          prFlg,
          isRecommendCv: isMainCv === false,
          surveyResponseLogId,
        });

        if (result.requiresLogin) {
          // ログイン誘導は LOGIN_PROMPT（buildWidgetLoginUrl 集約）側で発火する。ここでは発火しない。
          calendarModal.close();
          panel.showLoginGate(buildWidgetLoginUrl("APPOINTMENT_BOOKING", analyticsCtx));
          return;
        }
        if (result.duplicateCompany) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（重複）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showDuplicateCompanyAppointmentError(result.duplicateCompany.existingAppointmentId);
          return;
        }
        if (!result.success) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（バリデーション/システム等）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showErrorMessageBanner({ errorType: "SYSTEM", message: "エラーが発生しました。時間をおいて再度お試しください。" });
          return;
        }
        if (result.success) {
          // 効果分析ログ（イベントG＝ゴール、BR-AN-1）: アポリクエスト送信成功。
          trackAppointmentSubmit(analyticsCtx);
          lockCardAsCompleted(card, companyName, contentName, "アポイントリクエストを送信しました。", null);
          calendarModal.close();
        }
      },
    });
  } else {
    refreshConfirmButtonAction = renderSlotSelectionSection({
      container: bookingArea,
      panel,
      slots,
      meetingTypeSelection,
      messageInput,
      calendarModal,
      companyName,
      contentName,
      memberGroupId,
      // 効果分析ログ（イベントC、BR-AN-1）: 空き枠から日時候補を選択したとき SLOT_SELECT を発火する。
      onSlotSelectedForAnalytics: () => trackSlotSelect(analyticsCtx),
      onConfirm: async (slot, meetingType, message, visitPreferredLocation) => {
        const result = await confirmAppointment({
          memberGroupId,
          scheduledStartAt: slot.startAt,
          screenType,
          objectType,
          objectRootId,
          meetingType,
          message,
          visitPreferredLocation,
          prFlg,
          isRecommendCv: isMainCv === false,
          surveyResponseLogId,
        });

        if (result.requiresLogin) {
          // ログイン誘導は LOGIN_PROMPT（buildWidgetLoginUrl 集約）側で発火する。ここでは発火しない。
          panel.showLoginGate(buildWidgetLoginUrl("APPOINTMENT_BOOKING", analyticsCtx));
          return;
        }
        if (result.slotUnavailable) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（空き枠なし）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showErrorMessageBanner({ errorType: "BUSINESS", message: "その枠は埋まりました。他の候補をお選びください。" });
          return;
        }
        if (result.duplicateCompany) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（重複）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showDuplicateCompanyAppointmentError(result.duplicateCompany.existingAppointmentId);
          return;
        }
        if (!result.success) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（システム等）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showErrorMessageBanner({ errorType: "SYSTEM", message: "エラーが発生しました。時間をおいて再度お試しください。" });
          return;
        }
        if (result.appointmentId) {
          // 効果分析ログ（イベントG＝ゴール、BR-AN-1）: アポリクエスト送信成功（確定）。appointmentId を載せる。
          trackAppointmentSubmit({ ...analyticsCtx, appointmentId: result.appointmentId });
          lockCardAsCompleted(
            card, companyName, contentName, "アポイントリクエストを送信しました。",
            { appointmentId: result.appointmentId, scheduledStartAt: result.scheduledStartAt, scheduledEndAt: result.scheduledEndAt, exhibitorCompanyName: result.exhibitorCompanyName }
          );
          calendarModal.close();
        }
      },
      onRequest: async (desiredDateTimes, meetingType, message, visitPreferredLocation) => {
        const result = await submitAppointmentRequest({
          memberGroupId,
          desiredDateTime1: desiredDateTimes.desiredDateTime1,
          desiredDateTime2: desiredDateTimes.desiredDateTime2,
          desiredDateTime3: desiredDateTimes.desiredDateTime3,
          screenType,
          objectType,
          objectRootId,
          meetingType,
          message,
          visitPreferredLocation,
          prFlg,
          isRecommendCv: isMainCv === false,
          surveyResponseLogId,
        });

        if (result.requiresLogin) {
          // ログイン誘導は LOGIN_PROMPT（buildWidgetLoginUrl 集約）側で発火する。ここでは発火しない。
          calendarModal.close();
          panel.showLoginGate(buildWidgetLoginUrl("APPOINTMENT_BOOKING", analyticsCtx));
          return;
        }
        if (result.duplicateCompany) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（重複）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showDuplicateCompanyAppointmentError(result.duplicateCompany.existingAppointmentId);
          return;
        }
        if (!result.success) {
          // 効果分析ログ（イベントD、BR-AN-6）: 送信失敗（バリデーション/システム等）。
          trackAppointmentSubmitFailed({ ...analyticsCtx, failureKind: result.failureKind, businessErrorCode: result.businessErrorCode });
          showErrorMessageBanner({ errorType: "SYSTEM", message: "エラーが発生しました。時間をおいて再度お試しください。" });
          return;
        }
        if (result.success) {
          // 効果分析ログ（イベントG＝ゴール、BR-AN-1）: アポリクエスト送信成功（リクエスト）。
          trackAppointmentSubmit(analyticsCtx);
          lockCardAsCompleted(card, companyName, contentName, "アポイントリクエストを送信しました。", null);
          calendarModal.close();
        }
      },
    });
  }
}

export interface SlotSelectionSectionOptions {
  container: HTMLElement;
  panel: ReturnType<typeof createWidgetPanel>;
  slots: AppointmentSlot[];
  meetingTypeSelection: ReturnType<typeof renderMeetingTypeSelection>;
  messageInput: ReturnType<typeof renderMessageInput>;
  calendarModal: CalendarModalHandle;
  companyName?: string | null;
  contentName?: string | null;
  /** カレンダーの選択可否をアポ受付設定へ反映するために必要（未指定時はAPI取得を行わずフェイルセーフ表示）。 */
  memberGroupId?: string | null;
  /** スロット確定ボタンクリック時のハンドラ。 */
  onConfirm: (slot: AppointmentSlot, meetingType: MeetingType, message: string, visitPreferredLocation?: string) => Promise<void>;
  /** 「都合の良い日時がない場合はこちら」→ 候補確定後のリクエスト送信ハンドラ。 */
  onRequest: (desiredDateTimes: { desiredDateTime1: string; desiredDateTime2?: string; desiredDateTime3?: string }, meetingType: MeetingType, message: string, visitPreferredLocation?: string) => Promise<void>;
  /**
   * 効果分析ログ（イベントC、BR-AN-1）: 空き枠から日時候補を選択したときに呼ばれる（任意）。
   * 呼び出し元が SLOT_SELECT を発火する（文脈を持つのは呼び出し元のため）。日時実値は渡さない（BR-AN-8）。
   */
  onSlotSelectedForAnalytics?: () => void;
}

/**
 * 空き枠あり時のスロット選択セクションを構築する共通ロジック。
 * 単一CV系（bookingSectionUi）・複数CV系（multiCvAppointmentListUi）の両方から利用する。
 * @returns 確定ボタンのラベルを再計算する関数（商談形式切替時に外部から呼ぶ用）。
 */
export function renderSlotSelectionSection(options: SlotSelectionSectionOptions): () => void {
  const { container, slots, meetingTypeSelection, messageInput, calendarModal, companyName, contentName, memberGroupId, onConfirm, onRequest, onSlotSelectedForAnalytics } = options;

  // 「日程」ラベル（モック準拠: スロットリストの前に配置）
  container.appendChild(createElement("p", { className: "spica-slot-label", text: "日程" }));

  const calendar = renderSlotPicker(slots, { calendarModal, companyName: companyName ?? undefined, contentName: contentName ?? undefined, onSlotSelected: (slot) => {
    // 効果分析ログ（イベントC、BR-AN-1）: 日時候補選択を呼び出し元へ通知する（発火は呼び出し元）。
    onSlotSelectedForAnalytics?.();
    refreshConfirmButtonLabel(slot);
  } });
  container.appendChild(calendar.element);

  // モック準拠: スロット→メッセージ→確定ボタン→折りたたみ
  container.appendChild(messageInput.element);

  // 同意文言（確定ボタン直上、全モーダル共通）
  container.appendChild(renderConsentText());

  const confirmButton = createElement("button", { className: "spica-confirm-button", text: "この日時でリクエストする" }) as HTMLButtonElement;
  confirmButton.disabled = true;
  container.appendChild(confirmButton);

  // 対面商談時の注記（確定ボタン直下、モック準拠）
  const onsiteRequestNote = createElement("p", { className: "spica-onsite-request-note", text: "※ まだ確定ではありません。出展企業から日程確認のご連絡をします。" });
  onsiteRequestNote.hidden = true;
  container.appendChild(onsiteRequestNote);

  const refreshConfirmButtonLabel = (slotOverride?: AppointmentSlot): void => {
    const selectedSlot = slotOverride ?? calendar.getSelectedSlot();
    confirmButton.disabled = selectedSlot === null;
    if (!selectedSlot) {
      confirmButton.textContent = "この日時でリクエストする";
      onsiteRequestNote.hidden = true;
      return;
    }
    const isOnSite = meetingTypeSelection.getSelectedMeetingType() === "ON_SITE";
    const slotLabel = formatSlotButtonLabel(selectedSlot);
    confirmButton.textContent = `${slotLabel} でリクエストする`;
    onsiteRequestNote.hidden = !isOnSite;
  };

  // 確定ボタンのクリックハンドラは1つに一本化する（`.onclick`の再代入は行わない）。
  // `addEventListener`で登録したハンドラは`.onclick`代入では解除されないため、両方を有効にすると
  // 1クリックでスロット確定（onConfirm）とリクエスト（onRequest）の両方が発火し、商談が2件作成される。
  // これを防ぐため、submitModeで送信内容を排他的に切り替える（2026-09-04バグ修正）。
  let submitMode: "slot" | "request" = "slot";
  let requestValues: { desiredDateTime1: string; desiredDateTime2?: string; desiredDateTime3?: string } | null = null;

  calendar.element.addEventListener("click", () => {
    // リクエスト導線が有効な間はスロット選択によるラベル再計算を行わない（モード確定後は文言固定）。
    if (submitMode === "slot") refreshConfirmButtonLabel();
  });

  confirmButton.addEventListener("click", async () => {
    const { message, visitPreferredLocation } = messageInput.getValues();
    const meetingType = meetingTypeSelection.getSelectedMeetingType();

    if (submitMode === "request") {
      if (!requestValues) return;
      const restoreButton = setButtonLoading(confirmButton, "送信中...");
      try {
        await onRequest(requestValues, meetingType, message, visitPreferredLocation);
      } finally {
        restoreButton();
      }
      return;
    }

    const selectedSlot = calendar.getSelectedSlot();
    if (!selectedSlot) return;
    const restoreButton = setButtonLoading(confirmButton, "送信中...");
    try {
      await onConfirm(selectedSlot, meetingType, message, visitPreferredLocation);
    } finally {
      restoreButton();
    }
  });

  // BR-U6-4改訂: 空き枠がある場合でも、都合が合わない閲覧ユーザーのためにリクエスト導線を
  // 折りたたみで常設する（design-mockups準拠、中央カレンダーモーダルで展開）。
  const toggleButton = createElement("button", { className: "spica-request-toggle-btn", text: "都合の良い日時がない場合はこちら" });
  let requestModalBody: HTMLElement | null = null;

  // 候補確定後の静的表示領域（スロットリストと排他）
  const requestSummary = createElement("div");
  requestSummary.hidden = true;
  calendar.element.parentElement!.insertBefore(requestSummary, calendar.element.nextSibling);

  // 2026-09-30 バグ修正: 一度日時候補リクエストを確定すると通常リクエスト（空き枠選択）に
  // 戻れなくなる不具合への対応（リロードでしか復帰できなかった）。候補確定時に切り替えた
  // 表示・送信モードを、確定前の初期状態（空き枠選択）へ戻す処理を1つにまとめる。
  //  - 候補サマリ（requestSummary）を非表示にし中身をクリア
  //  - 空き枠リスト（calendar）を再表示
  //  - 送信モードをslotへ戻し、保持していた候補値を破棄
  //  - 確定ボタンを未選択状態（非活性・ラベル「この日時でリクエストする」）へリセット
  //  - 「都合の良い日時がない場合はこちら」トグルを再表示
  const restoreSlotSelection = (): void => {
    requestSummary.hidden = true;
    requestSummary.textContent = "";
    calendar.element.hidden = false;
    submitMode = "slot";
    requestValues = null;
    refreshConfirmButtonLabel();
    toggleButton.hidden = false;
  };

  toggleButton.addEventListener("click", () => {
    let readyPromise: Promise<void>;
    if (!requestModalBody) {
      requestModalBody = createElement("div");
      const { getValues, ready } = renderDesiredDateTimeCalendarInputs(requestModalBody, memberGroupId);
      readyPromise = ready;

      const confirmRequestBtn = createElement("button", { className: "spica-confirm-button spica-request-confirm-btn", text: "候補を確定する" });
      confirmRequestBtn.addEventListener("click", () => {
        const values = getValues();
        if (!values) return;
        // モーダルを閉じてカード本体に静的候補一覧を表示
        calendarModal.close();
        // スロットリストを非表示にし、静的候補を表示
        calendar.element.hidden = true;
        requestSummary.hidden = false;
        requestSummary.textContent = "";
        // 2026-09-30 バグ修正: 通常リクエスト（空き枠選択）へ戻すための「戻る」リンクを
        // 候補サマリの先頭に配置する（候補確定後は空き枠選択に戻る導線が無く、リロードでしか
        // 復帰できなかった不具合の対応。極力画面変更を抑えた控えめなテキストリンク）。
        const backLink = createElement("button", { className: "spica-request-back-link", text: "戻る" });
        backLink.addEventListener("click", restoreSlotSelection);
        requestSummary.appendChild(backLink);
        // 候補確定中は重複導線を避けるためトグルを隠す（restoreSlotSelectionで復帰時に再表示）。
        toggleButton.hidden = true;
        const labels = ["第1候補", "第2候補", "第3候補"];
        const vals = [values.desiredDateTime1, values.desiredDateTime2, values.desiredDateTime3];
        for (let i = 0; i < vals.length; i++) {
          if (!vals[i] && i > 0) continue;
          requestSummary.appendChild(createElement("p", { className: "spica-request-candidate-label", text: labels[i] }));
          requestSummary.appendChild(createElement("div", { className: "spica-proposal-candidate-value-static", text: vals[i] ? formatDesiredDateTimeLabel(vals[i]!) : "未選択" }));
        }
        // 確定ボタンをリクエスト送信モードへ切り替える（`.onclick`は使わず、共通のクリック
        // ハンドラがsubmitModeを見て排他的にonRequestを呼ぶ。2026-09-04バグ修正）。
        confirmButton.textContent = "この内容でリクエストする";
        confirmButton.disabled = false;
        submitMode = "request";
        requestValues = values;
      });
      requestModalBody.appendChild(confirmRequestBtn);
    } else {
      readyPromise = Promise.resolve();
    }
    // 対応可能ルールAPI取得完了（カレンダー本体の構築完了）を待ってからモーダルを開く。
    // 未待機だとcalendarModalUi.tsのreorderModalBodyがカレンダー要素未検出で並び替えを
    // スキップしてしまう（2026-09-02修正、非同期化に伴うモーダル並び替え崩れの対応）。
    void readyPromise.then(() => {
      calendarModal.openWithElement(requestModalBody!, () => {
        requestModalBody = null;
      }, { company: companyName ?? undefined, content: contentName ?? undefined, reorderBody: true });
    });
  });

  container.appendChild(toggleButton);

  return () => refreshConfirmButtonLabel();
}

export interface RequestSectionOptions {
  container: HTMLElement;
  panel: ReturnType<typeof createWidgetPanel>;
  calendarModal: CalendarModalHandle;
  meetingTypeSelection: ReturnType<typeof renderMeetingTypeSelection>;
  messageInput: ReturnType<typeof renderMessageInput>;
  companyName?: string | null;
  contentName?: string | null;
  /** カレンダーの選択可否をアポ受付設定へ反映するために必要（未指定時はAPI取得を行わずフェイルセーフ表示）。 */
  memberGroupId?: string | null;
  /** 候補確定→送信ハンドラ。 */
  onRequest: (desiredDateTimes: { desiredDateTime1: string; desiredDateTime2?: string; desiredDateTime3?: string }, meetingType: MeetingType, message: string, visitPreferredLocation?: string) => Promise<void>;
}

/**
 * 空き枠なし時のアポイントリクエストセクションを構築する共通ロジック。
 * 単一CV系（bookingSectionUi）・複数CV系（multiCvAppointmentListUi）の両方から利用する。
 */
export function renderRequestSection(options: RequestSectionOptions): void {
  const { container, calendarModal, meetingTypeSelection, messageInput, companyName, contentName, memberGroupId, onRequest } = options;

  const note = createElement("p", { className: "spica-request-note", text: "現在ご案内可能な日時がありません" });
  container.appendChild(note);

  const requestButton = createElement("button", { className: "spica-request-confirm-btn", text: "アポイントをリクエストする" });
  let requestModalBody: HTMLElement | null = null;
  let savedDesiredDateTimes: { desiredDateTime1: string; desiredDateTime2?: string; desiredDateTime3?: string } | null = null;

  // 候補確定後の静的表示領域（初期非表示）
  const requestSummaryArea = createElement("div");
  requestSummaryArea.hidden = true;
  container.appendChild(requestSummaryArea);

  // メッセージ入力欄＋送信ボタン（候補確定後に表示、モック準拠2段階フロー）
  const messageFieldsArea = createElement("div");
  messageFieldsArea.hidden = true;
  messageFieldsArea.appendChild(messageInput.element);
  const submitButton = createElement("button", { className: "spica-confirm-button", text: "この内容でリクエストする" });
  submitButton.addEventListener("click", () => {
    if (!savedDesiredDateTimes) return;
    const restoreButton = setButtonLoading(submitButton as HTMLButtonElement, "送信中...");
    const { message, visitPreferredLocation } = messageInput.getValues();
    void onRequest(savedDesiredDateTimes, meetingTypeSelection.getSelectedMeetingType(), message, visitPreferredLocation).finally(restoreButton);
  });
  // 同意文言（送信ボタン直上、全モーダル共通）
  messageFieldsArea.appendChild(renderConsentText());
  messageFieldsArea.appendChild(submitButton);

  requestButton.addEventListener("click", () => {
    let readyPromise: Promise<void>;
    if (!requestModalBody) {
      requestModalBody = createElement("div");
      const { getValues, ready } = renderDesiredDateTimeCalendarInputs(requestModalBody, memberGroupId);
      readyPromise = ready;
      const confirmRequestBtn = createElement("button", { className: "spica-confirm-button spica-request-confirm-btn", text: "候補を確定する" });
      confirmRequestBtn.addEventListener("click", () => {
        const values = getValues();
        if (!values) return;
        savedDesiredDateTimes = values;
        calendarModal.close();
        // 初期表示を非表示にし、候補確定後の表示に切替
        note.hidden = true;
        requestButton.hidden = true;
        requestSummaryArea.hidden = false;
        requestSummaryArea.textContent = "";
        const labels = ["第1候補", "第2候補", "第3候補"];
        const vals = [values.desiredDateTime1, values.desiredDateTime2, values.desiredDateTime3];
        for (let i = 0; i < vals.length; i++) {
          if (!vals[i] && i > 0) continue;
          requestSummaryArea.appendChild(createElement("p", { className: "spica-request-candidate-label", text: labels[i] }));
          requestSummaryArea.appendChild(createElement("div", { className: "spica-proposal-candidate-value-static", text: vals[i] ? formatDesiredDateTimeLabel(vals[i]!) : "未選択" }));
        }
        // メッセージ入力欄と送信ボタンを候補確定後に表示
        messageFieldsArea.hidden = false;
      });
      requestModalBody.appendChild(confirmRequestBtn);
    } else {
      readyPromise = Promise.resolve();
    }
    // 対応可能ルールAPI取得完了（カレンダー本体の構築完了）を待ってからモーダルを開く。
    // 未待機だとcalendarModalUi.tsのreorderModalBodyがカレンダー要素未検出で並び替えを
    // スキップしてしまう（2026-09-02修正、非同期化に伴うモーダル並び替え崩れの対応）。
    void readyPromise.then(() => {
      calendarModal.openWithElement(requestModalBody!, () => {
        requestModalBody = null;
      }, { company: companyName ?? undefined, content: contentName ?? undefined, reorderBody: true });
    });
  });
  container.appendChild(requestButton);
  container.appendChild(messageFieldsArea);
}

/**
 * 予約確定・リクエスト送信完了後、カード内を企業名・コンテンツ名を残したまま完了メッセージへ
 * 差し替える（design-mockups準拠）。日時確定済みの場合はICS/Googleカレンダーリンクも表示する。
 */
function lockCardAsCompleted(
  card: HTMLElement,
  companyName: string | null | undefined,
  contentName: string | null | undefined,
  message: string,
  confirmed: { appointmentId: string; scheduledStartAt?: string; scheduledEndAt?: string; exhibitorCompanyName?: string | null } | null
): void {
  card.textContent = "";
  card.appendChild(createElement("div", { className: "spica-company-name", text: companyName ?? "出展企業" }));
  card.appendChild(createElement("div", { className: "spica-content-name", text: contentName ?? "" }));
  card.appendChild(createElement("p", { className: "spica-proposal-noslot", text: message }));

  if (confirmed && confirmed.scheduledStartAt && confirmed.scheduledEndAt) {
    card.appendChild(
      renderCalendarAddActions({
        appointmentId: confirmed.appointmentId,
        scheduledStartAt: confirmed.scheduledStartAt,
        scheduledEndAt: confirmed.scheduledEndAt,
        exhibitorCompanyName: confirmed.exhibitorCompanyName,
      })
    );
  }
}
